"use client";

import { byBrand } from "@repo/tailwind-config/brand";
import React from "react";
import {
  NavHeader as BaseNavHeader,
  type NavHeaderTheme,
  mergeTheme,
} from "@repo/ui/v2";
import { HeadlineBodyText, SublineCondensedText } from "./KinsaTypography";
import Image from "next/image";
import kinsaHeaderImage from "public/kinsa-header.svg";
import backIcon from "public/icons/ChevronLeftIcon.svg";
import menuIcon from "public/icons/MenuIcon.svg";
import xcloseIcon from "public/icons/XCloseIcon.svg";
import { publishEventToSDK } from "@repo/sdk/publisher";
import {
  STATSIG_EVENTS,
  useStatsigClient,
  type RegisteredStatsigEvent,
} from "@repo/statsig";

const kinsaTheme: NavHeaderTheme = {
  container:
    "fixed top-0 left-0 right-0 z-10 bg-background border-b-[0.5px] border-foreground/5 pb-4 min-h-11",
  text: "text-foreground text-base font-normal text-center",
  button: "px-4",
  spacer: "w-6",
  subtitleContainer: "w-full text-center mt-2",
  subtitleText: "text-accent text-xs font-semibold",
};

export interface BrandedNavHeaderProps {
  showLogo?: boolean;
  showTitle?: boolean;
  title?: string;
  subtitle?: string;
  leftButtonType?: "back" | "menu";
  rightButtonType?: "menu" | "close";
  showCloseButton?: boolean;
  onClose?: () => void;
  onLeftClick?: () => void;
  onRightClick?: () => void;
  className?: string;
  logo?: React.ReactNode;
  theme?: NavHeaderTheme;
  /** Override the default menu dot icon (e.g. for dark backgrounds). */
  menuIconOverride?: React.ReactNode;
  /** Accessible name for the right action; it otherwise announces as "menu". */
  rightAriaLabel?: string;
}

export const BrandedNavHeader: React.FC<BrandedNavHeaderProps> = ({
  showLogo = true,
  showTitle = false,
  title,
  subtitle,
  leftButtonType,
  rightButtonType,
  showCloseButton,
  onClose,
  onLeftClick,
  onRightClick,
  className,
  logo: logoProp,
  theme,
  menuIconOverride,
  rightAriaLabel,
}) => {
  const { logEvent } = useStatsigClient();

  const defaultClose = () => {
    logEvent({ eventName: "home_dismissed" } as RegisteredStatsigEvent);
    logEvent({
      eventName: STATSIG_EVENTS.UI_DISMISSED,
      value: "home",
    });
    publishEventToSDK({ topic: "dismiss", data: "success" });
  };

  const defaultLogo = byBrand({
    owlet: (
      <Image
        src="/icons/owlet/logo.png"
        alt="Owlet OnCall"
        height={18}
        width={128}
        priority
      />
    ),
    kinsa: (
      <Image src={kinsaHeaderImage} alt="Kinsa Logo" height={24} priority />
    ),
  });
  const logo = logoProp ?? defaultLogo;

  const defaultMenuIcon = (
    <Image src={menuIcon} alt="Menu" width={4} height={16} unoptimized />
  );
  const menuIconEl = menuIconOverride ?? defaultMenuIcon;

  const leftAction = leftButtonType
    ? {
        type: leftButtonType as "back" | "menu",
        icon:
          leftButtonType === "back" ? (
            <Image src={backIcon} alt="Back" height={18} unoptimized />
          ) : (
            menuIconEl
          ),
        onClick: onLeftClick,
      }
    : undefined;

  // Resolve right button: explicit rightButtonType takes precedence, then showCloseButton fallback
  const resolvedRightType =
    rightButtonType ?? (showCloseButton ? "close" : undefined);

  const rightAction = resolvedRightType
    ? {
        type: resolvedRightType as "menu" | "close",
        icon:
          resolvedRightType === "close" ? (
            <Image
              src={xcloseIcon}
              alt="Close"
              width={24}
              height={24}
              unoptimized
            />
          ) : (
            menuIconEl
          ),
        onClick:
          resolvedRightType === "close"
            ? onClose || defaultClose
            : onRightClick,
        ariaLabel: rightAriaLabel,
        className:
          resolvedRightType === "close"
            ? "px-4 text-foreground-muted hover:text-foreground transition-colors flex items-center justify-center"
            : undefined,
      }
    : undefined;

  const mergedTheme = mergeTheme(kinsaTheme, theme);

  return (
    <BaseNavHeader
      showLogo={showLogo}
      showTitle={showTitle}
      title={title}
      subtitle={subtitle}
      logo={logo}
      leftAction={leftAction}
      rightAction={rightAction}
      theme={mergedTheme}
      TypographyEl={HeadlineBodyText}
      SubtitleEl={SublineCondensedText}
      className={className}
    />
  );
};
